import { toast } from 'react-toastify'
import {
  usePinTarget,
  type PinTarget,
} from 'components/QuickAccess/usePinTarget'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import {
  ActionsDropdown,
  createPinToHomepageAction,
  type ActionItem,
} from './ActionsDropdown'

interface PinnableRowActionsProps {
  /** The entity this row pins to the homepage. */
  target: PinTarget
  /** Actions rendered before the Pin/Unpin item. */
  actionsBefore?: ActionItem[]
  /** Actions rendered after the Pin/Unpin item (e.g. Delete). */
  actionsAfter?: ActionItem[]
  /** Name shown in the confirmation toast, e.g. "John Smith". */
  pinLabel: string
  /** Draw a divider after the Pin/Unpin item. */
  pinSeparatorAfter?: boolean
  className?: string
  containerWidth?: number
}

// Per-row dropdown with a real Pin/Unpin to homepage action wired to the
// Quick Access pin API. The pin item is hidden entirely when the
// `enableQuickAccess` feature flag is off.
export function PinnableRowActions({
  target,
  actionsBefore = [],
  actionsAfter = [],
  pinLabel,
  pinSeparatorAfter,
  className,
  containerWidth,
}: PinnableRowActionsProps) {
  const { enableQuickAccess } = useFeatureFlagService()
  const { isPinned, toggle, isPending } = usePinTarget(target)

  const pinAction: ActionItem = {
    ...createPinToHomepageAction(() => {
      toggle()
      toast.success(
        isPinned
          ? `Unpinned ${pinLabel} from homepage`
          : `Pinned ${pinLabel} to homepage`
      )
    }, isPinned),
    disabled: isPending,
    hasSeparatorAfter: pinSeparatorAfter,
  }

  return (
    <ActionsDropdown
      actions={[
        ...actionsBefore,
        ...(enableQuickAccess ? [pinAction] : []),
        ...actionsAfter,
      ]}
      className={className}
      containerWidth={containerWidth}
    />
  )
}
